import { useEffect, useState, type FormEvent, type PropsWithChildren } from 'react';
import { EmptyState } from '@renderer/shared/components/primitives';
import { useShellStore } from '@renderer/app/state/shell-store';
import { getDesktopBridgeDiagnostic } from '@renderer/lib/desktop-api';
import {
  getWebConsoleSession,
  isWebConsoleMode,
  loginWebConsole
} from '@renderer/lib/web-console-api';
import { BridgeDiagnosticScreen } from './bridge-diagnostic-screen';
import { StorageDiagnosticScreen } from './storage-diagnostic-screen';

function applyShellPreferences(): void {
  const preferences = useShellStore.getState().preferences;
  document.documentElement.dataset.motion = preferences.reduceMotion ? 'reduce' : 'full';
  document.documentElement.dataset.density = preferences.navigationDensity;
}

export function AppBootstrapProvider({ children }: PropsWithChildren): JSX.Element | null {
  const { bootStatus, isBootstrapping, error, errorStatus, preferences, load, resetBootstrap } = useShellStore();
  const bridgeDiagnostic = getDesktopBridgeDiagnostic();
  const webConsoleMode = isWebConsoleMode();
  const [authState, setAuthState] = useState<'checking' | 'authenticated' | 'unauthenticated'>(
    webConsoleMode ? 'checking' : 'authenticated'
  );

  useEffect(() => {
    if (!webConsoleMode && bridgeDiagnostic.status === 'ready') {
      void load();
    }
  }, [bridgeDiagnostic.status, load, webConsoleMode]);

  useEffect(() => {
    if (!webConsoleMode) {
      return;
    }

    let active = true;

    getWebConsoleSession()
      .then((session) => {
        if (!active) {
          return;
        }

        setAuthState(session.authenticated ? 'authenticated' : 'unauthenticated');
      })
      .catch(() => {
        if (active) {
          setAuthState('unauthenticated');
        }
      });

    return () => {
      active = false;
    };
  }, [webConsoleMode]);

  useEffect(() => {
    if (webConsoleMode && authState === 'authenticated') {
      void load();
    }
  }, [authState, load, webConsoleMode]);

  useEffect(() => {
    if (webConsoleMode && authState === 'authenticated' && errorStatus === 401) {
      setAuthState('unauthenticated');
    }
  }, [authState, errorStatus, webConsoleMode]);

  useEffect(() => {
    applyShellPreferences();
  }, [preferences]);

  if (bridgeDiagnostic.status !== 'ready') {
    return <BridgeDiagnosticScreen diagnostic={bridgeDiagnostic} />;
  }

  if (webConsoleMode && authState === 'checking') {
    return null;
  }

  if (webConsoleMode && (authState === 'unauthenticated' || errorStatus === 401)) {
    return (
      <WebConsoleLoginScreen
        initialError={
          errorStatus === 401
            ? 'Sesiunea consolei a expirat sau nu a putut fi pastrata. Deschide consola prin https://console.alttvision.local si autentifica-te din nou.'
            : null
        }
        onAuthenticated={() => {
          resetBootstrap();
          setAuthState('authenticated');
        }}
      />
    );
  }

  if (isBootstrapping) {
    return null;
  }

  if (bootStatus && !bootStatus.database.ready) {
    return <StorageDiagnosticScreen bootStatus={bootStatus} />;
  }

  if (error) {
    return (
      <div className="av-boot-error">
        <EmptyState title="Renderer indisponibil" description={error} />
      </div>
    );
  }

  return <>{children}</>;
}

function WebConsoleLoginScreen({
  initialError,
  onAuthenticated
}: {
  initialError: string | null;
  onAuthenticated: () => void;
}): JSX.Element {
  const [email, setEmail] = useState('cosmin.lupsan@gmail.com');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(initialError);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setError(initialError);
  }, [initialError]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    useShellStore.getState().clearBootstrapError();

    try {
      await loginWebConsole(email, password);
      onAuthenticated();
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Autentificarea a esuat.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="av-web-login">
      <form className="av-web-login__panel" onSubmit={submit}>
        <div>
          <p className="av-web-login__eyebrow">Alttvision Console</p>
          <h1>Autentificare</h1>
        </div>
        <label className="av-web-login__field">
          <span>Email</span>
          <input
            autoComplete="username"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
        </label>
        <label className="av-web-login__field">
          <span>Parola</span>
          <input
            autoComplete="current-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
        </label>
        {error ? <p className="av-web-login__error">{error}</p> : null}
        <button className="av-web-login__button" type="submit" disabled={submitting}>
          {submitting ? 'Se verifica...' : 'Intra in consola'}
        </button>
      </form>
    </main>
  );
}
